import React from "react";

// Importera logotyper
import minimaxLogo from "@/assets/minimax-logo.webp";
import afterEffectsLogo from "@/assets/after-effects-logo.png";
import premiereLogo from "@/assets/premiere-pro-logo.png";
import chatgptLogo from "@/assets/chatgpt-logo-new.png";
import claudeLogo from "@/assets/claude-logo.svg";
import elevenlabsLogo from "@/assets/elevenlabs-logo.svg";
import geminiLogo from "@/assets/gemini-logo.svg";
import heygenLogo from "@/assets/heygen-logo-new.png";
import midjourneyLogo from "@/assets/midjourney-logo.png";
import nanoBananaLogo from "@/assets/nano-banana-logo.png";

interface LogoCarouselProps {
  className?: string;
  /** 0-1 justerar hur "silvrig" ton som appliceras */
  tintIntensity?: number;
  /** Antal sekunder för en full loop (högre = långsammare) */
  animationSpeed?: number;
}

interface LogoItem {
  src: string;
  alt: string;
}

const logoItems: LogoItem[] = [
  { src: minimaxLogo, alt: "Minimax" },
  { src: afterEffectsLogo, alt: "Adobe After Effects" },
  { src: premiereLogo, alt: "Adobe Premiere Pro" },
  { src: chatgptLogo, alt: "ChatGPT" },
  { src: claudeLogo, alt: "Claude AI" },
  { src: elevenlabsLogo, alt: "ElevenLabs" },
  { src: geminiLogo, alt: "Google Gemini" },
  { src: heygenLogo, alt: "HeyGen" },
  { src: midjourneyLogo, alt: "Midjourney" },
  { src: nanoBananaLogo, alt: "Nano Banana" },
];

const LogoCarousel: React.FC<LogoCarouselProps> = ({
  className = "",
  tintIntensity = 0.9,
  animationSpeed = 40, // Långsammare animering
}) => {
  // Duplicera loggor flera gånger för sömlös loop
  const duplicated = [...logoItems, ...logoItems, ...logoItems];

  // Make black parts gray and keep logos bright
  const uniformStyle: React.CSSProperties = {
    filter: `saturate(0) brightness(3.0) contrast(0.7)`,
  };

  return (
    <div className={`py-2 overflow-hidden ${className}`} aria-label="Kundlogotyper">
      {/* Använd full bredd på mobil, 4/5 på desktop */}
      <div className="w-full sm:w-4/5 mx-auto relative">
        {/* Själva flödet */}
        <div
          className="flex gap-8 items-center justify-center"
          style={{
            animation: `scroll-horizontal ${animationSpeed}s linear infinite`,
            width: 'max-content',
          }}
        >
          {duplicated.map((logo, i) => {
            // Gör Adobe logotyper extra små
            const isAdobeLogo = ["Adobe After Effects", "Adobe Premiere Pro"].includes(logo.alt);
            // Minska specifika logotyper
            const isSmaller = ["HeyGen", "ChatGPT"].includes(logo.alt);
            // Gör Gemini extra liten
            const isGemini = logo.alt === "Google Gemini";
            
            let containerClass;
            if (isGemini) {
              containerClass = "flex-shrink-0 h-4 w-12 sm:h-5 sm:w-14 md:h-6 md:w-16 flex items-center justify-center";
            } else if (isAdobeLogo) {
              containerClass = "flex-shrink-0 h-4 w-10 sm:h-5 sm:w-12 md:h-5 md:w-14 flex items-center justify-center";
            } else if (isSmaller) {
              containerClass = "flex-shrink-0 h-5 w-14 sm:h-6 sm:w-16 md:h-7 md:w-18 flex items-center justify-center";
            } else {
              containerClass = "flex-shrink-0 h-6 w-16 sm:h-8 sm:w-20 md:h-9 md:w-22 flex items-center justify-center";
            }
            
            return (
              <div key={i} className={containerClass}>
                <img 
                  src={logo.src}
                  alt={logo.alt}
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  style={{
                    ...uniformStyle,
                    // Invert ChatGPT logo colors
                    ...(logo.alt === "ChatGPT" && { filter: uniformStyle.filter + " invert(1)" }),
                    // Make Midjourney logo white
                    ...(logo.alt === "Midjourney" && { filter: "saturate(0) brightness(5) contrast(0)" }),
                    // Make Claude logo white
                    ...(logo.alt === "Claude AI" && { filter: "saturate(0) brightness(5) contrast(0)" })
                  }}
                  aria-hidden="true" 
                />
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes scroll-horizontal {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </div>
  );
};

export default LogoCarousel;
